import React, { useContext, useCallback } from 'react'
import { useGoogleLogin } from '@react-oauth/google'

import { apiUsersSignInFailure, apiUsersSignInStart, context, useUsersApi } from '../../../../store'
import { image } from '../../../../lib/image'
import { settings } from '../../../../store/constants'
import { AnalyticsService } from '../../../../services/analytics'

// Decode a JWT ID token payload (base64url-encoded JSON)
function decodeIdToken(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(json)
}

const CustomButton = ({ onClick, title }) => {
  return (
    <button type="button" className="google-login-button" onClick={onClick}>
      <img src={image('./login_google.svg')} title="Google" alt="Google" />
      <div>{title}</div>
    </button>
  )
}

export const GoogleLoginButton = React.memo(props => {
  const { title } = props
  const { dispatch } = useContext(context)
  const { apiUserGoogleLogin } = useUsersApi()

  // Primary: use popup flow via useGoogleLogin (works when COOP doesn't interfere)
  const popupLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        })
        if (!res.ok) throw new Error(`Google userinfo failed: ${res.status}`)
        const userInfo = await res.json()

        const email = userInfo.email
        if (!email) throw new Error('No email returned from Google')

        // The access token is what the server verifies with Google; the
        // email and names here are only for display, and the server takes
        // the address from the token, not from us.
        apiUserGoogleLogin({
          access_token: tokenResponse.access_token,
          first_name: userInfo.given_name || userInfo.name || '',
          last_name: userInfo.family_name || ''
        })
      } catch (error) {
        console.error('[GoogleLogin] popup flow error:', error)
        dispatch(apiUsersSignInFailure('Google sign-in failed. Please try again.'))
      }
    },
    onError: error => {
      console.error('[GoogleLogin] popup onError:', error)
      dispatch(apiUsersSignInFailure('Google sign-in failed. Please try again.'))
    }
  })

  // Fallback: use GIS One Tap / credential flow (no popup, immune to COOP)
  const credentialLogin = useCallback(() => {
    // GA4 - the click is the choice, regardless of which Google flow ends
    // up resolving it. Tells us how often customers pick Google sign-in.
    AnalyticsService.trackSigninMethodSelected('google')
    const gis = window.google?.accounts?.id
    if (!gis) {
      // GIS script not loaded - fall back to popup
      popupLogin()
      return
    }

    dispatch(apiUsersSignInStart())

    gis.initialize({
      client_id: settings.googleAppId,
      callback: response => {
        try {
          // Decoded only to fill the name in the UI. The raw credential is
          // what goes to the server, which verifies it with Google and takes
          // the email and uid from there; a decoded payload is just text we
          // could have written ourselves.
          const payload = decodeIdToken(response.credential)
          if (!payload.email) throw new Error('No email returned from Google')

          apiUserGoogleLogin({
            credential: response.credential,
            first_name: payload.given_name || payload.name || '',
            last_name: payload.family_name || ''
          })
        } catch (error) {
          console.error('[GoogleLogin] credential flow error:', error)
          dispatch(apiUsersSignInFailure('Google sign-in failed. Please try again.'))
        }
      },
      auto_select: false,
      cancel_on_tap_outside: false
    })

    gis.prompt(notification => {
      // If One Tap can't display (e.g. user dismissed it before, cooldown period),
      // fall back to the popup flow
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        console.log('[GoogleLogin] One Tap unavailable, falling back to popup')
        popupLogin()
      }
    })
  }, [apiUserGoogleLogin, dispatch, popupLogin])

  return <CustomButton onClick={credentialLogin} title={title} />
})
